import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';
import { VersionType, SolutionType } from 'src/app/types/sales-dna.types';

@Component({
  selector: 'app-create-solutions',
  template: `
    <div class="create-solution-page">
      <!-- Page header -->
      <div class="pagehead">
        <h1>Create solution</h1>
        <p>
          A solution bundles your project, data and pipeline into one workspace
          your team can run.
        </p>
      </div>

      <!-- Stepper -->
      <div class="stepper">
        <div
          class="step"
          [class.active]="!configureProjectFlag"
          [class.done]="configureProjectFlag"
        >
          <span class="num">
            <svg
              *ngIf="configureProjectFlag; else step1Num"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <ng-template #step1Num>1</ng-template>
          </span>
          <span class="lbl">Name</span>
        </div>
        <div class="step-line"></div>
        <div class="step" [class.active]="configureProjectFlag">
          <span class="num">2</span>
          <span class="lbl">Add a project</span>
        </div>
      </div>

      <!-- Panel -->
      <div class="panel">
        <!-- STEP 1: Name -->
        <ng-container *ngIf="!configureProjectFlag">
          <div class="field">
            <label>Solution name</label>
            <input
              type="text"
              [(ngModel)]="solutionName"
              placeholder="e.g. product_recosting"
            />
            <div class="help">
              Letters, numbers and underscores. This becomes the workspace name.
            </div>
          </div>
          <div class="actions">
            <button
              class="btn-ghost"
              routerLink="/home/manage-solutions/mySolutions"
            >
              Cancel
            </button>
            <div class="action-right">
              <span class="why" *ngIf="solutionName.trim() === ''"
                >Enter a solution name to continue</span
              >
              <button
                class="btn-primary"
                [disabled]="solutionName.trim() === ''"
                (click)="createNewSolution()"
              >
                Continue →
              </button>
            </div>
          </div>
        </ng-container>

        <!-- STEP 2: Add a project -->
        <ng-container *ngIf="configureProjectFlag">
          <!-- Segmented control -->
          <div class="seg">
            <button
              type="button"
              [class.active]="newProjectFlag"
              (click)="selectNew()"
            >
              New project
            </button>
            <button
              type="button"
              [class.active]="existingProject"
              (click)="selectExisting()"
            >
              Use existing
            </button>
          </div>

          <!-- New project -->
          <ng-container *ngIf="newProjectFlag">
            <div class="field">
              <label>Project name</label>
              <input
                type="text"
                [(ngModel)]="projectName"
                placeholder="e.g. recost_q3"
              />
              <div class="help">
                A solution can hold several projects (e.g. one per costing run).
              </div>
            </div>
            <label class="check-row">
              <input
                type="checkbox"
                class="check-input"
                [(ngModel)]="refreshData"
              />
              <span class="cbox" [class.on]="refreshData">
                <svg
                  *ngIf="refreshData"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              Refresh data before each run
            </label>
            <div class="actions">
              <span class="why" *ngIf="projectName.trim() === ''"
                >Enter a project name to continue</span
              >
              <span *ngIf="projectName.trim() !== ''"></span>
              <button
                class="btn-primary"
                [disabled]="projectName.trim() === ''"
                (click)="createNewProject()"
              >
                Create project →
              </button>
            </div>
          </ng-container>

          <!-- Use existing -->
          <ng-container *ngIf="existingProject">
            <div class="field">
              <label>Select projects</label>
              <mat-select
                class="boxed-select"
                [formControl]="existingProjectSelected"
                multiple
                placeholder="Choose projects to copy"
              >
                <mat-select-trigger>
                  {{ existingProjectSelected.value?.[0] || '' }}
                  <span
                    *ngIf="(existingProjectSelected.value?.length || 0) > 1"
                    class="example-additional-selection"
                  >
                    (+{{ (existingProjectSelected.value?.length || 0) - 1 }}
                    {{
                      existingProjectSelected.value?.length === 2
                        ? 'other'
                        : 'others'
                    }})
                  </span>
                </mat-select-trigger>
                <div *ngFor="let solution of optionList | keyvalue" class="ml-4">
                  {{ solution.key }}:
                  <div
                    *ngFor="let version of solution.value | keyvalue"
                    class="ml-6"
                  >
                    {{ version.key }}:
                    <mat-option
                      *ngFor="let project of version.value"
                      [value]="project['project_id']"
                      class="text-sm ml-6"
                    >
                      {{ project['project_name'] }}
                    </mat-option>
                  </div>
                </div>
              </mat-select>
              <div class="help">
                Copy one or more existing projects into this solution.
              </div>
            </div>
            <div class="actions">
              <span class="why" *ngIf="!existingProjectSelected.valid"
                >Select at least one project to continue</span
              >
              <span *ngIf="existingProjectSelected.valid"></span>
              <button
                class="btn-primary"
                [disabled]="!existingProjectSelected.valid"
                (click)="copyExistingProject()"
              >
                Create →
              </button>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  `,
  styleUrls: ['./create-solutions.component.scss'],
})
export class CreateSolutionsComponent {
  solutionName = '';
  configureProjectFlag: boolean = false;
  refreshData = false;
  projectName = '';
  newProjectFlag: boolean = false;
  existingProject: boolean = false;
  existingProjectSelected = new FormControl('', Validators.required);
  optionList: { [key: string]: { [key: string]: any } } = {};
  currentSelectedSolution: any;
  currentSelectedVersion: any;
  constructor(
    private solutionServices: SolutionsAppService,
    private snackBar: SharedFeaturesService,
    private router: Router
  ) {}


  createNewSolution() {
    this.solutionServices
      .createSolution({ solution_name: this.solutionName.trim().toLowerCase() })
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            'Solution Created Successfully',
            'Close',
            'success-snackbar'
          );
          let solution: SolutionType = response;
          let temp = {
            stage: 'dev',
            solution_id: solution['solution_id'],
          };
          this.currentSelectedSolution = solution;
          this.createNewVersion(temp,solution);
        },
        error: (error) => {
          this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
        },
      });
  }

  createNewVersion(version: any,solution:any) {
    this.solutionServices.createVersion(version,solution).subscribe({
      next: (response: any) => {
        this.snackBar.openSnackBar(
          'Version created',
          'Close',
          'success-snackbar'
        );
        let version: VersionType = response
        this.currentSelectedVersion = version;
        this.solutionServices.getAvailableSolution().subscribe({
          next: (response: any) => {
            let temp = response;
            temp = temp.filter(
              (obj: any) =>
                obj['solution_id'] !==
                this.currentSelectedSolution['solution_id']
            );
            for (const solution of temp) {
              this.solutionServices.getAvailableVersion(solution).subscribe({
                next: (response: any) => {
                  for (const version of response) {
                    this.getProjects(solution, version);
                  }
                },
              });
            }
          },
        });
        this.configureProjectFlag = true;
        // Default to "New project" selected when entering step 2.
        this.newProjectFlag = true;
        this.existingProject = false;
      },
      error: (error) => {
        this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
      },
    });
  }

  getProjects(solution: any, version: any) {
    this.solutionServices
      .getProjectsInVersion(solution,version)
      .subscribe({
        next: (response: any) => {
          if (response.length > 0) {
            if (!this.optionList[solution['solution_name']]) {
              this.optionList[solution['solution_name']] = {};
            }

            this.optionList[solution['solution_name']][
              version['version_name']
            ] = response;
          }
        },
        error: (error: any) => {
          console.error('Error fetching projects:', error);
        },
      });
  }

  createNewProject() {
    let temp = {
      project_name: this.projectName.trim().toLowerCase(),
      version_id: this.currentSelectedVersion?.version_id,
      refresh_data_flag: this.refreshData,
    };
    this.solutionServices.createProject(this.currentSelectedSolution,this.currentSelectedVersion,temp).subscribe({
      next: (response: any) => {
        this.snackBar.openSnackBar(
          'project created successfully',
          'Close',
          'success-snackbar'
        );
        this.router.navigate([
          '/home/manage-solutions/mySolutions/' +
            this.currentSelectedSolution?.solution_id +
            '/' +
            this.currentSelectedVersion?.version_id +
            '/' +
            response?.project_id+
            '/configurePipeline',
        ]);
      },
      error: (error) => {
        this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
      },
    });
  }

  copyExistingProject() {
    let temp = {
      project_ids_to_copy: this.existingProjectSelected.value,
    };
    this.solutionServices.copyProjects(this.currentSelectedSolution,this.currentSelectedVersion,temp).subscribe({
      next: (response: any) => {
        this.snackBar.openSnackBar(
          response['msg'],
          'Close',
          'success-snackbar'
        );
        this.router.navigate([
          '/home/manage-solutions/mySolutions/' +
            this.currentSelectedSolution?.solution_id,
        ]);
      },
      error: (error) => {
        this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
      },
    });
  }

  selectNew() {
    this.newProjectFlag = true;
    this.existingProject = false;
  }

  selectExisting() {
    this.existingProject = true;
    this.newProjectFlag = false;
  }
}
